/**
 * Opportunity Detail Page - REDESIGNED
 * Layout: Title → Type Badges → Creator/Collab → Two-Column (Details | Description) → Contact
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import Gallery from '../components/Gallery';
import Navbar from '../components/Navbar';
import { navigateBack, cameFrom } from '../utils/navigationHistory';
import { formatDate } from '../utils/dateFormatter';
import '../styles/colors.css';

const OpportunityDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [opportunity, setOpportunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [creator, setCreator] = useState(null);
  const [collaborators, setCollaborators] = useState([]);

  useEffect(() => {
    fetchOpportunity();
  }, [id]);

  const fetchOpportunity = async () => {
    try {
      const response = await apiClient.get(`/opportunities/${id}`);
      if (response.data.success) {
        setOpportunity(response.data.opportunity);
        
        try {
          const creatorResponse = await apiClient.get(`/users/${response.data.opportunity.created_by}`);
          if (creatorResponse.data.success) {
            setCreator(creatorResponse.data.user);
          }
        } catch (err) {
          console.error('Failed to load creator:', err);
        }

        try {
          const collabResponse = await apiClient.get(`/opportunities/${id}/collaborators`);
          if (collabResponse.data.success) {
            setCollaborators(collabResponse.data.collaborators);
          }
        } catch (err) {
          console.error('Failed to load collaborators:', err);
        }
      }
    } catch (err) {
      setError('Opportunity not found');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this opportunity?')) return;
    setDeleting(true);
    try {
      const response = await apiClient.delete(`/opportunities/${id}`);
      if (response.data.success) {
        alert('Opportunity deleted successfully');
        navigate('/opportunities');
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete opportunity');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div style={{ minHeight: '100vh', background: 'var(--bg-dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-light)' }}>
          Loading opportunity...
        </div>
      </>
    );
  }

  if (error || !opportunity) {
    return (
      <>
        <Navbar />
        <div style={{ minHeight: '100vh', background: 'var(--bg-dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '20px' }}>
          <p style={{ color: 'var(--accent-danger)', fontSize: '18px' }}>{error || 'Opportunity not found'}</p>
          <button onClick={() => navigate('/opportunities')} className="btn btn-secondary">Back to Opportunities</button>
        </div>
      </>
    );
  }

  const isCreator = user && user.id === opportunity.created_by;

  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          .opportunity-title {
            font-size: 32px !important;
          }

          .opportunity-details-grid {
            display: flex !important;
            flex-direction: column !important;
          }

          .opportunity-info-card {
            padding: 24px !important;
            margin-bottom: 24px !important;
          }

          .opportunity-description-card {
            padding: 24px !important;
          }

          .opportunity-meta-line {
            display: flex !important;
            flex-direction: column !important;
            gap: 8px !important;
            align-items: flex-start !important;
          }
        }
      `}</style>
      <Navbar />
      <div style={{ minHeight: '100vh', background: 'var(--bg-dark)', paddingTop: '140px', paddingBottom: '60px' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 20px' }}>

          <h1 className="opportunity-title" style={{ color: 'var(--text-light)', fontSize: '48px', fontWeight: '700', marginBottom: '20px', lineHeight: '1.2' }}>
            {opportunity.title}
          </h1>

          <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
            <span className="badge badge-primary" style={{ fontSize: '14px', padding: '8px 16px' }}>Opportunity</span>
            {opportunity.looking_for && opportunity.looking_for.map((type, i) => (
              <span key={i} style={{ background: 'var(--smoke-gray)', color: 'var(--text-light)', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '700', textTransform: 'capitalize' }}>
                {type.replace(/_/g, ' ')}
              </span>
            ))}
          </div>

          <div className="opportunity-meta-line" style={{ color: 'var(--text-light)', fontSize: '15px', marginBottom: '32px', opacity: 0.8 }}>
            <div>
              {creator && (
                <span>
                  By{' '}
                  <a href={`/users/${creator.id}`} onClick={(e) => { e.preventDefault(); navigate(`/users/${creator.id}`); }} 
                     style={{ color: 'var(--accent-primary)', textDecoration: 'underline', cursor: 'pointer' }}>
                    {creator.display_name}
                  </a>
                </span>
              )}
              {collaborators.length > 0 && (
                <span>
                  {' · Collaborators: '}
                  {collaborators.filter(c => c.collaboration_status === 'accepted').map((c, i, arr) => (
                    <span key={c.id}>
                      {c.collaborator_id ? (
                        <a href={`/users/${c.collaborator_id}`} onClick={(e) => { e.preventDefault(); navigate(`/users/${c.collaborator_id}`); }} 
                           style={{ color: 'var(--accent-primary)', textDecoration: 'underline', cursor: 'pointer' }}>
                          {c.collaborator_name}
                        </a>
                      ) : c.collaborator_name}
                      {i < arr.length - 1 && ', '}
                    </span>
                  ))}
                </span>
              )}
            </div>
            <div>
              Posted {formatDate(opportunity.created_at)}
              {opportunity.updated_at !== opportunity.created_at && (
                <span> · Updated {formatDate(opportunity.updated_at)}</span>
              )}
            </div>
          </div>

          <div className="opportunity-details-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '32px', marginBottom: '32px' }}>
            
            <div className="opportunity-info-card" style={{ background: 'var(--bg-card)', borderRadius: '12px', padding: '32px', boxShadow: 'var(--shadow-card)' }}>
              <div style={{ fontSize: '16px', lineHeight: '1.8', color: 'var(--text-dark)' }}>
                <p style={{ marginBottom: '12px' }}>
                  <strong>Location:</strong> {opportunity.city}, {opportunity.state}
                </p>
                
                {opportunity.travel_distance && (
                  <p style={{ marginBottom: '12px' }}>
                    <strong>Radius:</strong> {opportunity.travel_distance} miles
                  </p>
                )}

                <p style={{ marginBottom: '12px' }}>
                  <strong>Dates:</strong> {formatDate(opportunity.opportunity_start)}
                  {opportunity.opportunity_end && ` - ${formatDate(opportunity.opportunity_end)}`}
                </p>

                <p style={{ marginBottom: '12px' }}>
                  <strong>Compensation:</strong>{' '}
                  {opportunity.compensation === 'yes' 
                    ? (opportunity.compensation_amount || 'Yes') 
                    : opportunity.compensation === 'no' 
                    ? 'No' 
                    : 'TBD'}
                </p>
              </div>
            </div>

            <div className="opportunity-description-card" style={{ background: 'var(--bg-card)', borderRadius: '12px', padding: '32px', boxShadow: 'var(--shadow-card)' }}>
              <h2 style={{ margin: '0 0 20px 0', fontSize: '24px', fontWeight: '700', color: 'var(--text-dark)' }}>Description</h2>
              <p style={{ whiteSpace: 'pre-wrap', lineHeight: '1.7', fontSize: '16px', color: 'var(--text-dark)' }}>
                {opportunity.description}
              </p>
            </div>
          </div>

          {opportunity.contact_info && (
            <div style={{ background: 'var(--bg-card)', borderRadius: '12px', padding: '32px', marginBottom: '32px', boxShadow: 'var(--shadow-card)' }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '20px', fontWeight: '700', color: 'var(--text-dark)' }}>Contact Information</h3>
              <p style={{ fontSize: '16px', color: 'var(--text-dark)', lineHeight: '1.6' }}>{opportunity.contact_info}</p>
            </div>
          )}

          {opportunity.image_gallery_urls && opportunity.image_gallery_urls.length > 0 && (
            <div style={{ marginBottom: '32px' }}>
              <h2 style={{ color: 'var(--text-light)', fontSize: '24px', fontWeight: '700', marginBottom: '20px' }}>Gallery</h2>
              <Gallery images={opportunity.image_gallery_urls} />
            </div>
          )}

          {isCreator && (
            <div style={{ background: 'rgba(0, 217, 255, 0.1)', border: '2px solid var(--accent-primary)', borderRadius: '12px', padding: '24px', marginTop: '32px' }}>
              <p style={{ color: 'var(--text-light)', marginBottom: '16px', fontSize: '16px' }}><strong>You created this opportunity</strong></p>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button onClick={() => navigate(`/opportunities/${id}/edit`)} className="btn btn-primary">Edit Opportunity</button>
                <button onClick={handleDelete} disabled={deleting} className="btn btn-danger">{deleting ? 'Deleting...' : 'Delete Opportunity'}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default OpportunityDetailPage;
